if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      pendingMediaId = null;

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Preload ALL media before early return — mobile has no thumbnails so
        // without this preloadAllMedia() was never called on mobile devices
        this.preloadAllMedia();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      preloadAllMedia() {
        if (!this.elements.viewer) return;

        // ---- Step 1: Preload images already in the DOM ----
        this.elements.viewer.querySelectorAll('[data-media-id] img').forEach((img) => {
          // Switch lazy → eager so browser begins downloading immediately
          if (img.loading === 'lazy') img.loading = 'eager';

          if (!img.complete) {
            // img.decode() forces loading even for visibility:hidden images —
            // mobile Safari often ignores loading="eager" on hidden elements
            img.decode().catch(() => {});

            // <link rel="preload"> as extra push — respects srcset/sizes so the
            // browser fetches exactly the right resolution for this device
            if (img.src || img.srcset) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              if (img.srcset) {
                link.setAttribute('imagesrcset', img.srcset);
                if (img.sizes) link.setAttribute('imagesizes', img.sizes);
              } else if (img.src) {
                link.href = img.src;
              }
              document.head.appendChild(link);
            }
          }
        });

        // ---- Step 2: Preload variant images NOT yet in DOM ----
        // When hide_variants is enabled in theme settings, only the currently
        // selected variant's image is present in the initial HTML. All other
        // colour images are injected by updateMedia() only after the first
        // section-rendering fetch — too late to avoid a loading flash.
        //
        // Solution: read the explicit image URLs embedded by main-product.liquid
        // in the product-instant-media JSON and pre-fetch them via Image() objects
        // so the HTTP cache is warm before the user ever taps a colour swatch.
        try {
          const sectionId = this.id.replace('MediaGallery-', '');
          const dataEl = document.getElementById(`product-instant-media-${sectionId}`);
          if (!dataEl) return;

          const data = JSON.parse(dataEl.textContent);
          const preloadImages = data.preloadImages; // array of CDN URLs at width=800
          if (!Array.isArray(preloadImages) || !preloadImages.length) return;

          // Compute the srcset width that matches this device's actual pixel needs.
          // Dawn uses widths: 246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946
          const dpr = Math.min(window.devicePixelRatio || 1, 3);
          const neededPx = Math.round(window.innerWidth * dpr);
          const srcsetWidths = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
          const targetWidth = srcsetWidths.find((w) => w >= neededPx) || 1946;

          // Preload each image at the correct resolution.
          // Liquid embedded URLs at width=800; we swap in the device-optimal width
          // so the preloaded file matches the srcset URL the browser will actually use.
          this._variantPreloaders = [];
          const seenUrls = new Set();

          preloadImages.forEach((url800) => {
            if (!url800 || seenUrls.has(url800)) return;
            seenUrls.add(url800);

            // Replace the Liquid-generated width=800 with the optimal width
            const url = url800.replace(/([?&])width=\d+/, `$1width=${targetWidth}`);
            const img = new Image();
            img.src = url;
            this._variantPreloaders.push(img); // keep reference to prevent GC
          });
        } catch (e) {
          // Non-fatal — page works fine, just without the extra preloading
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Track which media is pending so rapid clicks don't cause stale switches
        this.pendingMediaId = mediaId;

        const doSwitch = () => {
          // Bail if a newer setActiveMedia call has superseded this one
          if (this.pendingMediaId !== mediaId) return;
          this.pendingMediaId = null;

          this.elements.viewer.querySelectorAll('[data-media-id].is-active').forEach((el) => el.classList.remove('is-active'));
          activeMedia.classList.add('is-active');

          this.preventStickyHeader();
          this.playActiveMedia(activeMedia);

          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        };

        // Find the main image in this media item
        const img = activeMedia.querySelector('.product__modal-opener--image img') || activeMedia.querySelector('img');

        if (img && !img.complete) {
          // Image not yet loaded — wait for it before switching to avoid white flash.
          // User sees the current image a bit longer rather than a white blank.
          const onReady = () => {
            img.removeEventListener('load', onReady);
            img.removeEventListener('error', onReady);
            doSwitch();
          };
          img.addEventListener('load', onReady);
          img.addEventListener('error', onReady);
          return;
        }

        // Image already loaded (in cache) — switch instantly
        doSwitch();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}